/**
 * Контекст `sessionId` на время обработки JS-запроса по WebSocket (executeScript / getData).
 *
 * Middleware шлёт `coreSessionId` в топике `/client/task/js-request/{coreId}`; пока выполняется
 * обработчик в `GlobalProvider`, upload вложений может взять этот id через `getJsRequestSessionId()`.
 */
let activeJsRequestSessionId: string | null = null
/** Явный sessionId для sync upload вложений (getData/getFullData), приоритетнее WS-контекста. */
let attachmentUploadSessionOverride: string | null = null

/** Текущий `sessionId` активного WS JS-запроса или `null`. */
export function getJsRequestSessionId(): string | null {
  return activeJsRequestSessionId
}

/** SessionId, заданный для upload вложений в текущем sync-стеке (см. `withAttachmentUploadSessionOverride`). */
export function getAttachmentUploadSessionOverride(): string | null {
  return attachmentUploadSessionOverride
}

/**
 * Синхронный контекст sessionId для `uploadAttachmentFileSync` (внутри хука getData/getFullData).
 * Не сбрасывает родительский WS-контекст при `null`.
 */
export function withAttachmentUploadSessionOverride<T>(
  sessionId: string | null | undefined,
  fn: () => T,
): T {
  const prevOverride = attachmentUploadSessionOverride
  const prevActive = activeJsRequestSessionId
  const explicit = normalizePlatformSessionId(sessionId)
  const resolved = explicit ?? prevOverride ?? prevActive
  attachmentUploadSessionOverride = resolved
  if (resolved) {
    activeJsRequestSessionId = resolved
  }
  try {
    return fn()
  } finally {
    attachmentUploadSessionOverride = prevOverride
    activeJsRequestSessionId = prevActive
  }
}

/**
 * Выполняет функцию с подменой активного `sessionId` JS-запроса (стек через `try/finally`).
 *
 * @param sessionId — id из WS; пустые значения сбрасывают контекст
 */
export async function runWithJsRequestSessionId<T>(
  sessionId: string | null | undefined,
  fn: () => T | Promise<T>,
): Promise<T> {
  const prev = activeJsRequestSessionId
  const explicit = normalizePlatformSessionId(sessionId)
  // Вложенный вызов с null не должен затирать sessionId родительского WS-контекста.
  activeJsRequestSessionId = explicit ?? prev
  try {
    return await fn()
  } finally {
    activeJsRequestSessionId = prev
  }
}

/**
 * Платформенный core session id может быть отрицательным long (Java).
 * Невалиден только sentinel «нет сессии» (-1), не любое отрицательное число.
 */
export function isValidPlatformSessionId(value: unknown): boolean {
  if (value === null || value === undefined || value === '') return false
  if (value === -1 || value === '-1') return false
  const trimmed = String(value).trim()
  if (trimmed === '' || trimmed === '-1') return false
  return true
}

/** Нормализует id сессии в строку или `null`, если значение невалидно. */
export function normalizePlatformSessionId(value: unknown): string | null {
  if (!isValidPlatformSessionId(value)) return null
  return String(value)
}
